import { useNavigate } from 'react-router-dom';
import { useCallback, useState } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  TableHead,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ArrowForwardIos } from '@mui/icons-material';

import { TableContainer, TableTitle, TitleBox, TitleBoxStatus } from './styles';

import TenLogo from '../../../assets/horizontal-blue-logo.svg';

import { Company } from '../../../services/requests/empresas/types';
import { createLogRequest } from '../../../services/requests/app-logs/utils';
import { Tokens, User } from '../../../services';

import useAuthStore from '../../../store/auth';
import useCompanyStore from '../../../store/companies';

import { InfoCard } from '../../../components/InfoCard';
import { LoadingBackdrop } from '../../../components/LoadingBackdrop';
import { LogoCompanies } from '../../../components/LogoCompanies';

import { getNumberOfUsers } from '../../../utils/nameHelpers';

interface ListagemEmpresasProps {
  listagemEmpresas: Company[];
  pageCompanies: number;
  sizeCompanies: number;
  countCompanies: number | undefined;
  setSizeCompanies: (type: number) => void;
  setPageCompanies: (type: number) => void;
}

export const ListagemEmpresas = ({
  listagemEmpresas,
  pageCompanies,
  sizeCompanies,
  countCompanies,
  setSizeCompanies,
  setPageCompanies,
}: ListagemEmpresasProps) => {
  const navigate = useNavigate();

  const user = useAuthStore(state => state.user);
  const setUser = useAuthStore(state => state.setUserAuthenticated);
  const tokens = useAuthStore(state => state.tokens);
  const selectedCompany = useCompanyStore(state => state.selectedCompany);
  const setSelectedCompany = useCompanyStore(state => state.setSelectedCompany);
  const setMenuSelected = useAuthStore(state => state.setMenuSelected);

  const [loading, setLoading] = useState(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageCompanies(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSizeCompanies(+event.target.value);
    setPageCompanies(0);
  };

  const defaultLabelDisplayedRows = ({ from, to, count }: any) => {
    return `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`;
  };

  const handleChangeCompany = useCallback(
    async (company: Company, event: any) => {
      try {
        setLoading(true);
        event.stopPropagation();

        const userInCompany = company?.users?.find(u => u.doc === user?.doc);

        if (userInCompany) {
          const userEdited = {
            ...user,
            profile: userInCompany?.profile,
          };

          setUser(userEdited as User, tokens as Tokens);
        } else {
          setUser(user as User, tokens as Tokens);
        }

        await createLogRequest({
          action: 'Acessou',
          company_id: selectedCompany?.id as string,
          description: `Acessou a plataforma na empresa ${selectedCompany?.name}`,
          local: 'Acessos',
          name: user?.name as string,
          user_id: user?.id as string,
        });

        setSelectedCompany(company);
        setMenuSelected('portal');
        navigate('/home/reunioes');

        setLoading(false);
      } catch (error) {
        console.log(error);
      }
    },
    [
      navigate,
      selectedCompany?.id,
      selectedCompany?.name,
      setMenuSelected,
      setSelectedCompany,
      setUser,
      tokens,
      user,
    ],
  );

  return (
    <>
      <TableContainer>
        {loading && (
          <LoadingBackdrop
            text="Alterando para"
            component={selectedCompany?.name}
            loading={loading}
          />
        )}
        {listagemEmpresas?.length > 0 ? (
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <TableHead
              sx={{
                color: '#000000',
                fontWeight: '400',
                fontSize: '20px',
                margin: '20px 0 15px 24px',
                display: 'table-caption',
              }}
            >
              Empresas
            </TableHead>
            <TableHead>
              <TableRow sx={{ width: 'fit-content' }}>
                <TableCell
                  align="left"
                  sx={{
                    alignSelf: 'center',
                    height: '48px',
                    padding: '10px 10px 10px 0px',
                    paddingLeft: '20px',
                  }}
                >
                  <TableTitle>NOME</TableTitle>
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    alignSelf: 'center',
                    height: '48px',
                    padding: '10px 10px 10px 0px',
                    paddingLeft: '20px',
                  }}
                >
                  <TableTitle>ID EMPRESA</TableTitle>
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    alignSelf: 'center',
                    height: '48px',
                    padding: '10px 10px 10px 0px',
                  }}
                >
                  <TableTitle>CNPJ</TableTitle>
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    alignSelf: 'center',
                    height: '48px',
                    padding: '10px 10px 10px 0px',
                  }}
                >
                  <TableTitle>USUÁRIOS</TableTitle>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    alignSelf: 'center',
                    height: '48px',
                    padding: '10px 10px 10px 0px',
                    width: '200px',
                  }}
                >
                  <TableTitle>STATUS</TableTitle>
                </TableCell>
                <TableCell align="center" />
              </TableRow>
            </TableHead>
            {user?.profile === 'UN1' ? (
              <TableBody>
                {listagemEmpresas
                  // ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  ?.map(row => {
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={row?.id}
                        onClick={() => navigate(`detalhes-empresa/${row?.id}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell
                          scope="row"
                          padding="normal"
                          align="left"
                          sx={{
                            height: '50px',
                            padding: '0',
                            paddingLeft: '20px',
                            maxWidth: '20vw',
                          }}
                        >
                          <Box width="100%" display="flex" alignItems="center">
                            <Box width="50px" mr="14px">
                              <LogoCompanies
                                url={row?.logo_url ? row?.logo_url : TenLogo}
                                boxWidth="100%"
                                height="40px"
                                maxWidth="100%"
                                width=""
                                justifyContent="center"
                              />
                            </Box>
                            <Tooltip title={row?.name}>
                              <TitleBox>
                                {row?.fancy_name ?? row?.name}
                              </TitleBox>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell
                          scope="row"
                          padding="normal"
                          align="left"
                          sx={{
                            height: '50px',
                            //   width: '350px',
                            padding: '0',
                            paddingLeft: '20px',
                          }}
                        >
                          <TitleBox>{row?.id?.slice(0, 8)}</TitleBox>
                        </TableCell>

                        <TableCell
                          scope="row"
                          align="left"
                          sx={{
                            height: '50px',
                            padding: '0',
                          }}
                        >
                          <TitleBox>{row?.doc}</TitleBox>
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            height: '50px',
                            padding: '0',
                          }}
                        >
                          <TitleBox>
                            {getNumberOfUsers(row?.users?.length ?? 0)}
                          </TitleBox>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            height: '50px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <TitleBoxStatus status={row?.status}>
                            {row?.status
                              ? 'Empresa ativa'
                              : 'Empresa desativada'}
                          </TitleBoxStatus>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            height: '50px',
                            padding: '0',
                          }}
                        >
                          {row?.status && (
                            <IconButton>
                              <ArrowForwardIos
                                onClick={event =>
                                  handleChangeCompany(row, event)
                                }
                                sx={{ color: '#206da7' }}
                              />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            ) : (
              <TableBody>
                {listagemEmpresas
                  // ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  ?.map(row => {
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={row?.id}
                        onClick={() => navigate(`detalhes-empresa/${row?.id}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell
                          scope="row"
                          padding="normal"
                          align="left"
                          sx={{
                            height: '50px',
                            padding: '0',
                            paddingLeft: '20px',
                          }}
                        >
                          <Box width="100%" display="flex" alignItems="center">
                            <Box width="50px" mr="14px">
                              <LogoCompanies
                                url={row?.logo_url ? row?.logo_url : TenLogo}
                                boxWidth="100%"
                                height="100%"
                                maxWidth="100%"
                                width=""
                                justifyContent="start"
                              />
                            </Box>
                            <Tooltip title={row?.name}>
                              <TitleBox>
                                {row?.fancy_name ?? row?.name}
                              </TitleBox>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell
                          scope="row"
                          padding="normal"
                          align="left"
                          sx={{
                            height: '50px',
                            padding: '0',
                            paddingLeft: '20px',
                          }}
                        >
                          <TitleBox>{row?.id}</TitleBox>
                        </TableCell>

                        <TableCell
                          scope="row"
                          align="left"
                          sx={{
                            height: '50px',
                            padding: '0',
                          }}
                        >
                          <TitleBox>{row?.doc}</TitleBox>
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            height: '50px',
                            padding: '0',
                          }}
                        >
                          <TitleBox>
                            {getNumberOfUsers(row?.users?.length ?? 0)}
                          </TitleBox>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            height: '50px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <TitleBoxStatus status={row?.status}>
                            {row?.status
                              ? 'Empresa ativa'
                              : 'Empresa desativada'}
                          </TitleBoxStatus>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            height: '50px',
                            padding: '0',
                          }}
                        >
                          {row?.status && (
                            <IconButton>
                              <ArrowForwardIos
                                onClick={event =>
                                  handleChangeCompany(row, event)
                                }
                                sx={{ color: '#206da7' }}
                              />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            )}
          </Table>
        ) : (
          <Box display="flex" justifyContent="center" minHeight="50vh">
            <InfoCard title="Nenhuma empresa encontrada!" type="info" />
          </Box>
        )}

        {listagemEmpresas?.length > 0 && (
          <TablePagination
            backIconButtonProps={{ disabled: pageCompanies === 0 }}
            nextIconButtonProps={{
              disabled:
                (sizeCompanies === 5 && listagemEmpresas?.length < 5) ||
                (sizeCompanies === 10 && listagemEmpresas?.length < 10) ||
                (sizeCompanies === 25 && listagemEmpresas?.length < 25),
            }}
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={countCompanies as number}
            rowsPerPage={sizeCompanies}
            page={pageCompanies}
            labelRowsPerPage="Linhas por página"
            onPageChange={handleChangePage}
            labelDisplayedRows={defaultLabelDisplayedRows}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>
    </>
  );
};
